import React, { useEffect, useState } from 'react';
import { useApi } from '../hooks/useApi';

export default function AdminArchivePage() {
  const api = useApi();
  const [years, setYears] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ year: '', champion: '', runnerUp: '', finalScore: '', notes: '', rawEntries: '', logoUrl: '' });
  const [logoPreview, setLogoPreview] = useState(null);
  const [saveStatus, setSaveStatus] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    api.getArchiveYears()
      .then(data => setYears(Array.isArray(data) ? data : []))
      .catch(err => { console.warn('Archive:', err.message); });
  }, []);

  function startNew() {
    setEditing('new');
    setLogoPreview(null);
    setForm({ year: '', champion: '', runnerUp: '', finalScore: '', notes: '', rawEntries: '', logoUrl: '' });
    setSaveStatus('');
    setError('');
  }

  async function startEdit(year) {
    setEditing(year);
    setError('');
    setSaveStatus('');
    try {
      const data = await api.getArchiveYear(year);
      const raw = (data.entries || []).map(e =>
        e.bracket ? (e.name + '\t' + e.bracket + '\t' + e.score) : (e.name + '\t' + e.score)
      ).join('\n');
      setLogoPreview(null);
      setForm({ year: String(data.year), champion: data.champion || '', runnerUp: data.runnerUp || '', finalScore: data.finalScore || '', notes: data.notes || '', rawEntries: raw, logoUrl: data.logoUrl || '' });
    } catch (err) { setError(err.message); }
  }

  function parseEntries(raw) {
    if (!raw) return [];
    return raw.trim().split('\n').filter(l => l.trim()).map(line => {
      const parts = line.split('\t').map(p => p.trim());
      if (parts.length >= 3) return { name: parts[0], bracket: parts[1], score: Number(parts[2]) || 0 };
      if (parts.length === 2) return { name: parts[0], bracket: '', score: Number(parts[1]) || 0 };
      return null;
    }).filter(Boolean);
  }

  function parseHtmlFile(html) {
    const parser = new window.DOMParser();
    const doc = parser.parseFromString(html, 'text/html');

    // Extract year — supports 1990s and 2000s
    const bodyText = doc.body ? doc.body.textContent : '';
    const bodyMatch = bodyText.match(/March\s+Madness\s+((?:19|20)\d{2})/i);
    const titleEl = doc.querySelector('title');
    const titleMatch = titleEl ? titleEl.textContent.match(/(?:19|20)\d{2}/) : null;
    const year = bodyMatch ? bodyMatch[1] : (titleMatch ? titleMatch[0] : null);

    const rows = doc.querySelectorAll('tr');
    const entries = [];
    let skipped = false;

    rows.forEach(function(row) {
      const cells = row.querySelectorAll('td');
      if (cells.length < 2) return;

      // Get clean text from each cell
      function cellText(cell) {
        return cell.textContent.trim().replace(/\s+/g, ' ').replace(/<[^>]*>/g, '').trim();
      }

      const c1 = cellText(cells[0]);

      // Score may be in last column — handles both 2-column and 3-column formats
      // Find the last cell that contains a number
      let scoreCell = null;
      for (var i = cells.length - 1; i >= 1; i--) {
        const t = cellText(cells[i]);
        if (t && !isNaN(Number(t))) {
          scoreCell = t;
          break;
        }
      }

      // Skip header row
      if (!skipped && (c1.toLowerCase() === 'name' || (scoreCell && scoreCell.toLowerCase() === 'score') || c1.toLowerCase() === 'score')) {
        skipped = true;
        return;
      }

      if (!c1 || !scoreCell) return;

      const score = Number(scoreCell);
      if (isNaN(score)) return;

      // Strip parenthetical tiebreaker scores from name e.g. "Fatty (166)" -> "Fatty"
      const cleanName = c1.replace(/\s*\(\d+\)\s*$/, '').trim();

      entries.push({ name: cleanName, bracket: '', score: score });
    });

    return { entries: entries, year: year };
  }

  function handleFileUpload(e) {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = function(ev) {
      try {
        const result = parseHtmlFile(ev.target.result);
        if (result.entries.length > 0) {
          const raw = result.entries.map(function(x) { return x.name + '\t' + x.score; }).join('\n');
          setForm(function(f) { return Object.assign({}, f, { rawEntries: raw, year: f.year || result.year || '' }); });
          setSaveStatus('Parsed ' + result.entries.length + ' entries' + (result.year ? ' (' + result.year + ')' : ''));
          setError('');
        } else {
          setError('No entries found in file.');
        }
      } catch (err) { setError('Parse error: ' + err.message); }
    };
    reader.readAsText(file, 'windows-1252');
  }

  async function handleSave() {
    setError(''); setSaveStatus('Saving...');
    const entries = parseEntries(form.rawEntries);
    if (!form.year) { setError('Year is required'); setSaveStatus(''); return; }
    if (!entries.length) { setError('No entries found'); setSaveStatus(''); return; }
    try {
      await api.saveArchiveYear(form.year, { champion: form.champion, runnerUp: form.runnerUp, finalScore: form.finalScore, notes: form.notes, entries: entries, logoUrl: logoPreview || form.logoUrl || null });
      setSaveStatus('Saved ' + entries.length + ' entries for ' + form.year);
      api.getArchiveYears().then(data => setYears(Array.isArray(data) ? data : [])).catch(() => {});
    } catch (err) { setError(err.message); setSaveStatus(''); }
  }

  async function handleDelete(year) {
    if (!window.confirm('Delete ' + year + ' archive?')) return;
    try {
      await api.deleteArchiveYear(year);
      setYears(function(y) { return y.filter(function(x) { return x.year !== year; }); });
      if (editing === year) setEditing(null);
    } catch (err) { setError(err.message); }
  }

  const preview = parseEntries(form.rawEntries);

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '1.8rem', color: 'var(--blue)', letterSpacing: '2px' }}>ARCHIVE MANAGEMENT</h2>
        <button className="btn btn-primary" onClick={startNew}>+ Add Year</button>
      </div>

      {error && <div className="alert alert-error">{error}</div>}
      {saveStatus && <div className="alert alert-success">{saveStatus}</div>}

      <div className="grid-2" style={{ gap: 20, alignItems: 'start' }}>

        <div className="card">
          <div className="card-header"><h3 className="card-title" style={{ fontSize: '1.1rem' }}>ARCHIVED YEARS</h3></div>
          {years.length === 0
            ? <p style={{ color: 'var(--gray)', fontFamily: 'var(--font-condensed)', padding: '16px 0' }}>No archive years yet.</p>
            : <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {years.map(y => (
                  <div key={y.year} onClick={() => startEdit(y.year)} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', background: editing === y.year ? 'rgba(75,139,190,0.15)' : 'var(--navy-light)', border: '1px solid ' + (editing === y.year ? 'var(--blue)' : 'var(--slate)'), borderRadius: 8, cursor: 'pointer' }}>
                    <div>
                      <span style={{ fontFamily: 'var(--font-display)', fontSize: '1.4rem', color: 'var(--cream)' }}>{y.year}</span>
                      <span style={{ fontFamily: 'var(--font-condensed)', fontSize: '0.8rem', color: 'var(--gray)', marginLeft: 10 }}>{y.entryCount} entries{y.champion ? ' · 🏆 ' + y.champion : ''}</span>
                    </div>
                    <button className="btn btn-danger btn-sm" onClick={e => { e.stopPropagation(); handleDelete(y.year); }}>Delete</button>
                  </div>
                ))}
              </div>
          }
        </div>

        {editing && (
          <div className="card">
            <div className="card-header">
              <h3 className="card-title" style={{ fontSize: '1.1rem' }}>{editing === 'new' ? 'ADD NEW YEAR' : 'EDIT ' + editing}</h3>
            </div>

            <div className="grid-2" style={{ gap: 12, marginBottom: 16 }}>
              <div className="form-group" style={{ margin: 0 }}>
                <label className="form-label">Year</label>
                <input className="form-input" type="number" placeholder="e.g. 2024" value={form.year} onChange={e => setForm(f => ({ ...f, year: e.target.value }))} disabled={editing !== 'new'} />
              </div>
              <div className="form-group" style={{ margin: 0 }}>
                <label className="form-label">Championship Score</label>
                <input className="form-input" placeholder="e.g. Florida 65, Houston 63" value={form.finalScore} onChange={e => setForm(f => ({ ...f, finalScore: e.target.value }))} />
              </div>
              <div className="form-group" style={{ margin: 0 }}>
                <label className="form-label">NCAA Champion</label>
                <input className="form-input" placeholder="e.g. Florida" value={form.champion} onChange={e => setForm(f => ({ ...f, champion: e.target.value }))} />
              </div>
              <div className="form-group" style={{ margin: 0 }}>
                <label className="form-label">NCAA Runner-Up</label>
                <input className="form-input" placeholder="e.g. Houston" value={form.runnerUp} onChange={e => setForm(f => ({ ...f, runnerUp: e.target.value }))} />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Notes (optional)</label>
              <input className="form-input" placeholder="e.g. 103 entries, first year with tiebreaker" value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} />
            </div>

            <div className="form-group">
              <label className="form-label">Year Logo (optional)</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                {(logoPreview || form.logoUrl) && (
                  <img src={logoPreview || form.logoUrl} alt="logo"
                    style={{ height: 48, width: 48, objectFit: 'contain', borderRadius: 6, border: '1px solid var(--slate)', background: 'white', padding: 2 }} />
                )}
                <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '7px 14px', background: 'var(--navy-light)', border: '1px solid var(--slate)', borderRadius: 8, cursor: 'pointer', fontFamily: 'var(--font-condensed)', fontSize: '0.85rem', color: 'var(--cream)' }}>
                  🖼 {logoPreview || form.logoUrl ? 'Replace Logo' : 'Upload Logo'}
                  <input type="file" accept="image/*" style={{ display: 'none' }}
                    onChange={e => {
                      const file = e.target.files[0];
                      if (!file) return;
                      if (file.size > 500 * 1024) { setError('Image must be under 500KB'); return; }
                      const reader = new FileReader();
                      reader.onload = ev => setLogoPreview(ev.target.result);
                      reader.readAsDataURL(file);
                    }} />
                </label>
                {(logoPreview || form.logoUrl) && (
                  <button className="btn btn-secondary btn-sm" onClick={() => { setLogoPreview(null); setForm(f => ({ ...f, logoUrl: '' })); }}>Remove</button>
                )}
                <span style={{ fontSize: '0.78rem', color: 'var(--gray)', fontFamily: 'var(--font-condensed)' }}>PNG/JPG/SVG · max 500KB · shown on archive page</span>
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Upload .htm File</label>
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '8px 16px', background: 'var(--blue)', borderRadius: 8, cursor: 'pointer', fontFamily: 'var(--font-condensed)', fontWeight: 700, fontSize: '0.9rem', color: 'white', letterSpacing: '1px', textTransform: 'uppercase' }}>
                📁 Choose File
                <input type="file" accept=".htm,.html" onChange={handleFileUpload} style={{ display: 'none' }} />
              </label>
              <span style={{ marginLeft: 12, fontSize: '0.8rem', color: 'var(--gray)' }}>Excel-exported HTML (index.htm format)</span>
            </div>

            <div style={{ textAlign: 'center', color: 'var(--gray)', fontFamily: 'var(--font-condensed)', margin: '8px 0' }}>— or paste manually —</div>

            <div className="form-group">
              <label className="form-label">Entries (Name [tab] Score per line)</label>
              <textarea className="form-input" rows={10} placeholder={"Tiger\t142\nJane Smith\t135"} value={form.rawEntries} onChange={e => setForm(f => ({ ...f, rawEntries: e.target.value }))} style={{ fontFamily: 'monospace', fontSize: '0.85rem', resize: 'vertical' }} />
              {preview.length > 0 && (
                <div style={{ marginTop: 6, fontSize: '0.8rem', color: 'var(--gray)', fontFamily: 'var(--font-condensed)' }}>
                  {preview.length} entries detected — first 5: {preview.slice(0, 5).map((e, i) => <span key={i} style={{ color: 'var(--cream)', marginRight: 8 }}>{e.name} ({e.score})</span>)}
                </div>
              )}
            </div>

            <div style={{ display: 'flex', gap: 12 }}>
              <button className="btn btn-primary" onClick={handleSave}>💾 Save Archive Year</button>
              <button className="btn btn-secondary" onClick={() => setEditing(null)}>Cancel</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
